import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { LANG_OPTIONS } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, Download } from "@phosphor-icons/react";

export default function AdminBar() {
  const [s, setS] = useState({
    bar_name: "",
    tagline: "",
    address: "",
    phone: "",
    show_cart_total: true,
    default_language: "va",
  });
  const [saving, setSaving] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    api
      .get("/settings/bar")
      .then((r) => setS({ show_cart_total: true, default_language: "va", ...r.data }))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    if (!s.bar_name.trim()) {
      toast.error("El nombre del bar es obligatorio");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/bar", {
        bar_name: s.bar_name,
        tagline: s.tagline,
        address: s.address,
        phone: s.phone,
        show_cart_total: s.show_cart_total,
        default_language: s.default_language,
      });
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const menuUrl = window.location.origin + "/";
  const allergensUrl = window.location.origin + "/carta/alergenos";
  const qrAllergensRef = useRef(null);
  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "carta-qr.png";
    a.click();
  };
  const downloadAllergensQr = () => {
    const canvas = qrAllergensRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "alergenos-qr.png";
    a.click();
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <div className="label-cap">carta digital</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Ajustes del bar</h1>
      </div>

      <div className="card-flat p-6 space-y-4">
        <Field label="Nombre del bar *">
          <Input
            value={s.bar_name}
            onChange={(e) => setS({ ...s, bar_name: e.target.value })}
            data-testid="bar-settings-name"
          />
        </Field>
        <Field label="Eslogan">
          <Input
            value={s.tagline}
            onChange={(e) => setS({ ...s, tagline: e.target.value })}
            data-testid="bar-settings-tagline"
          />
        </Field>
        <Field label="Dirección">
          <Input
            value={s.address}
            onChange={(e) => setS({ ...s, address: e.target.value })}
            data-testid="bar-settings-address"
          />
        </Field>
        <Field label="Teléfono">
          <Input
            value={s.phone}
            onChange={(e) => setS({ ...s, phone: e.target.value })}
            data-testid="bar-settings-phone"
          />
        </Field>
        <Field label="Idioma por defecto">
          <Select
            value={s.default_language}
            onValueChange={(v) => setS({ ...s, default_language: v })}
          >
            <SelectTrigger data-testid="bar-settings-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANG_OPTIONS.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-center justify-between border-t border-[var(--border)] pt-4">
          <div>
            <Label className="font-display font-semibold">Mostrar precios en el carrito</Label>
            <p className="text-[var(--text-secondary)] text-xs mt-1">
              Si se desactiva, se ocultan el total y precios de los artículos al cliente.
            </p>
          </div>
          <Switch
            checked={s.show_cart_total}
            onCheckedChange={(v) => setS({ ...s, show_cart_total: v })}
            data-testid="bar-settings-show-total"
          />
        </div>
        <div className="pt-2">
          <Button
            className="btn-primary"
            onClick={save}
            disabled={saving}
            data-testid="save-bar-settings-btn"
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>

      <div className="mt-10 border-t border-[var(--border)] pt-8">
        <div className="flex items-center gap-2 mb-2">
          <QrCode size={22} weight="duotone" className="text-[var(--primary)]" />
          <h2 className="font-display text-2xl font-bold tracking-tight">Código QR de la carta</h2>
        </div>
        <p className="text-[var(--text-secondary)] text-sm mb-6">
          Imprímelo y ponlo en las mesas para que los clientes abran la carta con el móvil.
        </p>
        <div className="flex flex-wrap items-center gap-6">
          <div ref={qrRef} className="bg-white p-4 rounded-md border border-[var(--border)]" data-testid="qr-code">
            <QRCodeCanvas value={menuUrl} size={180} level="M" marginSize={2} />
          </div>
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-secondary)] break-all max-w-[240px] font-mono">
              {menuUrl}
            </p>
            <Button
              className="btn-primary"
              onClick={downloadQr}
              data-testid="download-qr-btn"
            >
              <Download size={16} className="mr-2" />
              Descargar PNG
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-10 border-t border-[var(--border)] pt-8">
        <div className="flex items-center gap-2 mb-2">
          <QrCode size={22} weight="duotone" className="text-amber-600" />
          <h2 className="font-display text-2xl font-bold tracking-tight">Código QR de alérgenos</h2>
        </div>
        <p className="text-[var(--text-secondary)] text-sm mb-6">
          QR dedicado para clientes celíacos o con alergias. Enlaza a la página pública con filtros por alérgeno.
        </p>
        <div className="flex flex-wrap items-center gap-6">
          <div ref={qrAllergensRef} className="bg-white p-4 rounded-md border border-[var(--border)]" data-testid="qr-code-allergens">
            <QRCodeCanvas value={allergensUrl} size={180} level="M" marginSize={2} />
          </div>
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-secondary)] break-all max-w-[240px] font-mono">
              {allergensUrl}
            </p>
            <Button
              className="btn-primary"
              onClick={downloadAllergensQr}
              data-testid="download-allergens-qr-btn"
            >
              <Download size={16} className="mr-2" />
              Descargar PNG
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
